/**
 * Pixel geometry and column/lane layout math for the Cost Scheme board.
 *
 * Two independent geometries live here:
 *  1. Vertical timeline scale — {@link HOUR_PX} / {@link hoursToPx} map fractional hours to on-screen
 *     height; {@link HEAD_PAD} is the fixed padding above the first hour tick.
 *  2. Horizontal day columns — {@link computeDayLayout} packs a day's overlapping timed parts into
 *     side-by-side lanes; {@link computeDayFactors} + {@link gridTemplateColumns} turn per-day lane counts
 *     (and the collapsed-day set) into the CSS grid template. The accommodation and services strips reuse
 *     the same day factors so their percentage geometry stays aligned with the grid columns.
 */
import type { BoardPart } from './types';

/** Pixels per hour (linear vertical scale). */
export const HOUR_PX = 46;
/** Px reserved above the first hour tick / block area (day-column head padding). */
export const HEAD_PAD = 56;
/** Width factor of a collapsed day column. */
export const COLLAPSED_FACTOR = 0.16;

export const hoursToPx = (h: number): number => Math.max(0, h) * HOUR_PX;

export interface DayLane {
  part: BoardPart;
  lane: number;
}

export interface DayLayout {
  lanes: DayLane[];
  laneCount: number;
}

/** Greedy lane assignment for the timed parts of a single day (overlaps sit side by side). */
export function computeDayLayout(parts: BoardPart[]): DayLayout {
  const sorted = [...parts].sort(
    (a, b) => (a.startHour ?? 0) - (b.startHour ?? 0) || a.hours - b.hours
  );
  const laneEnds: number[] = [];
  const lanes = sorted.map((part) => {
    const start = part.startHour ?? 0;
    const end = start + part.hours;
    let lane = laneEnds.findIndex((laneEnd) => laneEnd <= start);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(end);
    } else {
      laneEnds[lane] = end;
    }
    return { part, lane };
  });
  return { lanes, laneCount: laneEnds.length || 1 };
}

/**
 * Per-day width factor. Collapsed days shrink to {@link COLLAPSED_FACTOR}; every extra lane widens the
 * day by 50 % of base. The accommodation and services strips receive the SAME factors so their percentage
 * geometry stays aligned with the grid columns.
 */
export function computeDayFactors(
  laneCounts: number[],
  collapsedDays: ReadonlySet<number>
): number[] {
  return laneCounts.map((lanes, index) =>
    collapsedDays.has(index) ? COLLAPSED_FACTOR : 1 + (lanes - 1) * 0.5
  );
}

/** Build the board grid's `grid-template-columns` (60px ruler gutter + one track per day). */
export function gridTemplateColumns(dayFactors: number[]): string {
  return '60px ' + dayFactors.map((f) => `minmax(${Math.round(252 * f)}px, ${f}fr)`).join(' ');
}
